import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Target, Eye, Heart, Award, Globe, Users, Clock, Lightbulb } from "lucide-react";

const values = [
  { icon: Target, title: "Precision", desc: "We deliver exact solutions — no bloat, no shortcuts. Every line of code serves a purpose." },
  { icon: Lightbulb, title: "Innovation", desc: "Staying ahead of the curve with cutting-edge technologies and creative problem-solving." },
  { icon: Heart, title: "Integrity", desc: "Transparent communication, honest timelines, and genuine partnership with every client." },
  { icon: Award, title: "Excellence", desc: "Quality isn't negotiable. We hold our work to the highest engineering standards." },
];

const milestones = [
  { year: "2022", title: "Company Founded", desc: "Scriptovative Technologies Pvt. Ltd. established in Hyderabad with a vision to deliver world-class software." },
  { year: "2023", title: "First Major Clients", desc: "Partnered with TheFirm26 and U&G Developers, delivering complex web platforms." },
  { year: "2024", title: "Vidyora Launched", desc: "Released Vidyora — our flagship school management system, serving educational institutions." },
  { year: "2025", title: "20+ Applications", desc: "Crossed the milestone of 20+ web applications delivered along with numerous websites." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/4 left-1/3 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono" data-testid="about-badge">
                ABOUT US
              </Badge>
              <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-[1.1] tracking-tight">
                Architecting the{" "}
                <span className="gradient-text">digital future.</span>
              </h1>
              <p className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed">
                Scriptovative Technologies Pvt. Ltd. is a Hyderabad-based software 
                development company specializing in building, hosting, and maintaining 
                high-performance web applications for businesses across industries.
              </p>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                Founded by a team of passionate engineers, we combine deep technical 
                expertise with a client-first approach to deliver software that drives 
                real business outcomes. With 20+ web applications and numerous websites 
                under our belt, we've proven our ability to turn complex ideas into 
                elegant, functional products.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-slate-800">
                <img
                  src="https://images.pexels.com/photos/1181405/pexels-photo-1181405.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Team collaboration"
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 glass rounded-xl p-5 shadow-xl">
                <div className="flex items-center gap-3">
                  <Globe className="w-8 h-8 text-indigo-400" />
                  <div>
                    <div className="font-['Outfit'] font-bold text-2xl text-slate-100">20+</div>
                    <div className="text-slate-400 text-xs">Web Apps Delivered</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="relative py-24 lg:py-32 bg-slate-900/30" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 lg:p-10 rounded-2xl bg-slate-900/50 border border-slate-800">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-6">
                <Eye className="w-6 h-6 text-indigo-400" />
              </div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-50 mb-4">Our Vision</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                To be the most trusted technology partner for businesses seeking 
                innovative, reliable, and scalable software solutions. We envision 
                a world where every business, regardless of size, has access to 
                enterprise-grade technology.
              </p>
            </div>
            <div className="p-8 lg:p-10 rounded-2xl bg-slate-900/50 border border-slate-800">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-cyan-400" />
              </div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-50 mb-4">Our Mission</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                To architect the digital future through precision software engineering. 
                We deliver end-to-end software solutions — from development to hosting 
                to maintenance — that empower businesses to achieve their goals faster, 
                more efficiently, and more securely.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-24 lg:py-32" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/10 text-xs font-mono">
              OUR VALUES
            </Badge>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              What drives <span className="gradient-text">us.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} data-testid={`value-card-${i}`} className="p-6 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-indigo-500/20 transition-all group text-center">
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4 mx-auto group-hover:bg-indigo-500/20 transition-colors">
                  <v.icon className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="font-['Outfit'] font-semibold text-slate-200 mb-2">{v.title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-24 lg:py-32 bg-slate-900/30" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono">
              OUR JOURNEY
            </Badge>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              Milestones that <span className="gradient-text">matter.</span>
            </h2>
          </div>
          <div className="relative">
            {/* Line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-slate-800 hidden sm:block" />
            <div className="space-y-12">
              {milestones.map((m, i) => (
                <div key={i} data-testid={`milestone-${i}`} className="relative flex gap-8">
                  <div className="hidden sm:flex flex-shrink-0 w-16 h-16 rounded-xl bg-slate-900 border border-slate-800 items-center justify-center relative z-10">
                    <span className="font-['JetBrains_Mono'] text-indigo-400 text-sm font-bold">{m.year}</span>
                  </div>
                  <div className="flex-1 p-6 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-indigo-500/20 transition-all">
                    <span className="sm:hidden font-['JetBrains_Mono'] text-indigo-400 text-xs font-bold">{m.year}</span>
                    <h3 className="font-['Outfit'] font-semibold text-slate-200 mb-2">{m.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-600/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
            Want to know more about <span className="gradient-text">our team?</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/team" data-testid="about-cta-team" className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5">
              Meet the Team
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" data-testid="about-cta-contact" className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 hover:bg-slate-800/50 px-8 py-3.5 rounded-full font-medium transition-all hover:-translate-y-0.5">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
